/*
 * Capital Raise Readiness Assessment — scoring & routing engine
 * Version 1.0
 *
 * Pure functions only: no DOM, no network, no globals mutated besides the
 * UMD export. This is what test/capital-raise-assessment-scoring.test.js
 * exercises directly under Node.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory(require('./capital-raise-assessment-config.js'));
  } else {
    root.ChadMCAssessmentScoring = factory(root.ChadMCAssessmentConfig);
  }
})(typeof self !== 'undefined' ? self : this, function (CONFIG) {
  'use strict';

  function round(n) {
    return Math.round(n);
  }

  function average(nums) {
    if (!nums.length) return 0;
    var sum = nums.reduce(function (a, b) { return a + b; }, 0);
    return sum / nums.length;
  }

  function questionById(id) {
    for (var i = 0; i < CONFIG.QUESTIONS.length; i++) {
      if (CONFIG.QUESTIONS[i].id === id) return CONFIG.QUESTIONS[i];
    }
    return null;
  }

  /* answers: { q1: 'A', q2: 'C', ... } (letter values as selected in the UI) */
  function scoreForQuestion(questionId, answers) {
    var q = questionById(questionId);
    if (!q || !q.scored) return null;
    var selected = answers[questionId];
    if (!selected) return null;
    var opt = q.options.filter(function (o) { return o.value === selected; })[0];
    return opt ? opt.score : null;
  }

  /*
   * Computes all six 0-100 category scores from raw answers, applying the
   * Investor Targeting / Q20 fit cap (spec section 5, Q20 special rule; and
   * hard constraint Rule 4).
   */
  function computeCategoryScores(answers) {
    var scores = {};
    CONFIG.CATEGORY_ORDER.forEach(function (categoryKey) {
      var cat = CONFIG.CATEGORIES[categoryKey];
      var qScores = cat.questionIds
        .map(function (qid) { return scoreForQuestion(qid, answers); })
        .filter(function (s) { return s !== null; });
      var raw = average(qScores);

      if (categoryKey === 'investor_targeting') {
        var fitScore = scoreForQuestion(CONFIG.INVESTOR_TARGETING_FIT_QUESTION_ID, answers);
        if (fitScore !== null && fitScore <= CONFIG.INVESTOR_TARGETING_FIT_CAP_THRESHOLD) {
          raw = Math.min(raw, CONFIG.INVESTOR_TARGETING_FIT_CAP_VALUE);
        }
      }

      scores[categoryKey] = raw;
    });
    return scores;
  }

  /* Weighted overall score (0-100), rounded to the nearest whole number. */
  function computeOverallScore(categoryScores) {
    var weighted = CONFIG.CATEGORY_ORDER.reduce(function (sum, key) {
      return sum + categoryScores[key] * CONFIG.CATEGORIES[key].weight;
    }, 0);
    return round(weighted);
  }

  function classificationForScore(overallScore) {
    return CONFIG.CLASSIFICATIONS.filter(function (c) {
      return overallScore >= c.min && overallScore <= c.max;
    })[0] || CONFIG.CLASSIFICATIONS[0];
  }

  function classificationByKey(key) {
    return CONFIG.CLASSIFICATIONS.filter(function (c) { return c.key === key; })[0];
  }

  /*
   * Applies hard constraint Rules 1-2: certain low category scores cap the
   * classification even when the raw weighted average would score higher.
   * Rule order and cap direction ("cannot exceed") are applied here.
   */
  function applyClassificationCaps(rawClassification, categoryScores) {
    var capped = rawClassification;
    var appliedCaps = [];
    CONFIG.CLASSIFICATION_CAP_RULES.forEach(function (rule) {
      if (categoryScores[rule.category] < rule.belowScore) {
        var capClass = classificationByKey(rule.capClassificationKey);
        if (capClass.max < capped.max) {
          capped = capClass;
        }
        appliedCaps.push({ category: rule.category, capClassificationKey: rule.capClassificationKey });
      }
    });
    return { classification: capped, appliedCaps: appliedCaps };
  }

  function foundationPriorityTriggered(categoryScores) {
    return CONFIG.FOUNDATION_PRIORITY_RULE.categories.some(function (key) {
      return categoryScores[key] < CONFIG.FOUNDATION_PRIORITY_RULE.belowScore;
    });
  }

  /*
   * Rule 5 (with override) + Rule 6: determine primary/secondary constraint.
   * Rule 7: flag when the two lowest are within NEAR_TIE_THRESHOLD points.
   */
  function determineConstraints(categoryScores) {
    var entries = CONFIG.CATEGORY_ORDER.map(function (key) {
      return { key: key, score: categoryScores[key] };
    });
    var sorted = entries.slice().sort(function (a, b) { return a.score - b.score; });

    var primary = sorted[0];
    var secondary = sorted[1];

    var targeting = entries.filter(function (e) { return e.key === 'investor_targeting'; })[0];
    var others = entries.filter(function (e) { return e.key !== 'investor_targeting'; });
    var minOthers = Math.min.apply(Math, others.map(function (e) { return e.score; }));
    var targetingIsClearOutlier = (minOthers - targeting.score) >= CONFIG.INVESTOR_TARGETING_OVERRIDE_MARGIN;

    if (targetingIsClearOutlier) {
      primary = targeting;
      var remaining = entries
        .filter(function (e) { return e.key !== 'investor_targeting'; })
        .sort(function (a, b) { return a.score - b.score; });
      secondary = remaining[0];
    }

    var closelyRelated = Math.abs(primary.score - secondary.score) <= CONFIG.NEAR_TIE_THRESHOLD;

    return {
      primary: primary.key,
      primaryScore: primary.score,
      secondary: secondary.key,
      secondaryScore: secondary.score,
      investorTargetingOverride: targetingIsClearOutlier,
      closelyRelated: closelyRelated
    };
  }

  function diagnosticTierForScore(score) {
    return CONFIG.DIAGNOSTIC_TIERS.filter(function (t) {
      return score >= t.min && score <= t.max;
    })[0].key;
  }

  function buildCategoryBreakdown(categoryScores) {
    return CONFIG.CATEGORY_ORDER.map(function (key) {
      var score = round(categoryScores[key]);
      var tier = diagnosticTierForScore(score);
      return {
        key: key,
        label: CONFIG.CATEGORIES[key].label,
        score: score,
        tier: tier,
        diagnosis: CONFIG.DIAGNOSTIC_LIBRARY[key][tier]
      };
    });
  }

  /*
   * Commercial route. See the "SPEC AMBIGUITY" note in
   * capital-raise-assessment-config.js above ROUTING_RULES — the nurture
   * vs. course boundary is an explicit, editable default pending
   * confirmation, not an authoritative reading of the brief.
   */
  function determineRoute(overallScore, categoryScores, answers) {
    var rules = CONFIG.ROUTING_RULES;
    var q25Score = scoreForQuestion('q25', answers);
    var companyStageOption = questionById('q1').options.filter(function (o) {
      return o.value === answers.q1;
    })[0];
    var companyStage = companyStageOption ? companyStageOption.dataValue : null;

    var meetsStrategyCriteria =
      overallScore >= rules.strategySession.minOverallScore &&
      categoryScores.financing_strategy >= rules.strategySession.minFinancingStrategyScore &&
      (
        (q25Score !== null && q25Score >= rules.strategySession.minQ25Score) ||
        categoryScores.raise_execution >= rules.strategySession.minRaiseExecutionScore
      );

    if (meetsStrategyCriteria) {
      return CONFIG.ROUTES.strategy_session;
    }

    var meetsNurtureCriteria =
      overallScore < rules.nurtureMaxOverallScore + 1 &&
      companyStage !== null &&
      rules.nurtureCompanyStages.indexOf(companyStage) !== -1;

    if (meetsNurtureCriteria) {
      return CONFIG.ROUTES.nurture;
    }

    return CONFIG.ROUTES.course;
  }

  /*
   * Full pipeline: raw answers in, complete structured result out.
   * This is the single function the app UI and the test suite both call.
   */
  function evaluate(answers) {
    var categoryScores = computeCategoryScores(answers);
    var overallScore = computeOverallScore(categoryScores);
    var rawClassification = classificationForScore(overallScore);
    var capResult = applyClassificationCaps(rawClassification, categoryScores);
    var constraints = determineConstraints(categoryScores);
    var breakdown = buildCategoryBreakdown(categoryScores);
    var route = determineRoute(overallScore, categoryScores, answers);

    var primaryEntry = breakdown.filter(function (b) { return b.key === constraints.primary; })[0];

    return {
      overallScore: overallScore,
      rawClassificationKey: rawClassification.key,
      classification: capResult.classification,
      classificationCapped: capResult.appliedCaps.length > 0,
      appliedClassificationCaps: capResult.appliedCaps,
      foundationPriorityTriggered: foundationPriorityTriggered(categoryScores),
      foundationPriorityMessage: CONFIG.FOUNDATION_PRIORITY_RULE.message,
      categoryScores: Object.keys(categoryScores).reduce(function (acc, k) {
        acc[k] = round(categoryScores[k]);
        return acc;
      }, {}),
      categoryBreakdown: breakdown,
      primaryConstraint: constraints.primary,
      primaryConstraintScore: round(constraints.primaryScore),
      secondaryConstraint: constraints.secondary,
      secondaryConstraintScore: round(constraints.secondaryScore),
      constraintsCloselyRelated: constraints.closelyRelated,
      investorTargetingOverrideApplied: constraints.investorTargetingOverride,
      recommendations: CONFIG.RECOMMENDATION_LIBRARY[constraints.primary],
      route: route,
      routeCopy: CONFIG.ROUTE_COPY[route],
      primaryConstraintDiagnosis: primaryEntry ? primaryEntry.diagnosis : null
    };
  }

  return {
    computeCategoryScores: computeCategoryScores,
    computeOverallScore: computeOverallScore,
    classificationForScore: classificationForScore,
    applyClassificationCaps: applyClassificationCaps,
    foundationPriorityTriggered: foundationPriorityTriggered,
    determineConstraints: determineConstraints,
    diagnosticTierForScore: diagnosticTierForScore,
    buildCategoryBreakdown: buildCategoryBreakdown,
    determineRoute: determineRoute,
    scoreForQuestion: scoreForQuestion,
    evaluate: evaluate
  };
});
